import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { determineGstType, getEntityState, getEntityGstin, gstBreakdownLines } from '@/utils/gstUtils';
import { 
  FileText, Download, Eye, CheckCircle, Clock, RefreshCw, Plus, 
  Search, Edit, XCircle, AlertTriangle, Building2, DollarSign, Ban,
  Trash2, RotateCcw, Archive, AlertOctagon, MessageSquare, ChevronDown, ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  { value: 'voided', label: 'Voided', color: 'bg-red-100 text-red-800', icon: XCircle }
];

const BILLING_CYCLES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'two_years', label: 'Two Years' },
  { value: 'three_years', label: 'Three Years' }
];

export default function AdminInvoices() {
  const { canDelete, hasPermission } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0, voided: 0, deleted: 0, total_amount: 0 });
  const [partners, setPartners] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });
  const [showDeleted, setShowDeleted] = useState(false);
  
  // Permission checks
  const canViewInvoice = hasPermission('invoices:view');
  const canCreateInvoice = hasPermission('invoices:create');
  const canEditInvoice = hasPermission('invoices:edit');
  const canVoidInvoice = hasPermission('invoices:void');
  const canDeleteInvoice = hasPermission('invoices:delete') && canDelete;

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Dialog states
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Invoice Queries state
  const [queries, setQueries] = useState([]);
  const [queriesOpen, setQueriesOpen] = useState(false);
  const [queryViewOpen, setQueryViewOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [resolveSubmitting, setResolveSubmitting] = useState(false);

  // Form states
  const [createForm, setCreateForm] = useState({
    partner_id: '',
    product_name: '',
    description: '',
    billing_cycle: 'monthly',
    quantity: 1,
    amount: ''
  });
  const [editForm, setEditForm] = useState({});
  const [voidReason, setVoidReason] = useState('');
  const [deleteForm, setDeleteForm] = useState({
    delete_type: 'soft',
    refund: false,
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchInvoices();
    fetchQueries();
  }, [statusFilter, partnerFilter, startDate, endDate, pagination.page, showDeleted]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page !== 1) {
        setPagination(prev => ({ ...prev, page: 1 }));
      } else {
        fetchInvoices();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchInvoices = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (partnerFilter !== 'all') params.append('partner_id', partnerFilter);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (showDeleted) params.append('include_deleted', 'true');
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);

      const response = await axios.get(`${API}/admin/invoices?${params}`);
      setInvoices(response.data.invoices);
      setStats(response.data.stats);
      setPartners(response.data.partners || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        total_pages: response.data.total_pages
      }));
    } catch (error) {
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchQueries = async () => {
    try {
      const res = await axios.get(`${API}/admin/invoice-queries`);
      setQueries(res.data || []);
    } catch { /* silent */ }
  };

  const handleResolveQuery = async (action) => {
    setResolveSubmitting(true);
    const endpoint = action === 'resolve'
      ? `${API}/invoice-queries/${selectedQuery.id}/resolve`
      : `${API}/invoice-queries/${selectedQuery.id}/reject-query`;
    try {
      await axios.put(endpoint, { note: resolveNote });
      toast.success(action === 'resolve' ? 'Query marked as resolved' : 'Query rejected');
      setQueryViewOpen(false);
      setResolveNote('');
      fetchQueries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setResolveSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) {
      toast.error('Only Admin or Support Admin can delete invoices');
      return;
    }
    if (!deleteForm.reason) {
      toast.error('Please provide a reason for deletion');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.delete(`${API}/admin/invoices/${selectedInvoice.id}`, {
        data: deleteForm
      });
      const msg = deleteForm.delete_type === 'soft' 
        ? `Invoice soft-deleted${response.data.refund_amount > 0 ? `. Refund: ₹${response.data.refund_amount.toLocaleString()}` : ''}`
        : `Invoice permanently deleted${response.data.refund_amount > 0 ? `. Refund: ₹${response.data.refund_amount.toLocaleString()}` : ''}`;
      toast.success(msg);
      setDeleteOpen(false);
      setDeleteForm({ delete_type: 'soft', refund: false, reason: '' });
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestore = async (invoice) => {
    try {
      const response = await axios.post(`${API}/admin/invoices/${invoice.id}/restore`);
      toast.success('Invoice restored successfully');
      if (response.data.warning) {
        toast.warning(response.data.warning, { duration: 6000 });
      }
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore invoice');
    }
  };

  const openDelete = (invoice) => {
    setSelectedInvoice(invoice);
    setDeleteForm({
      delete_type: 'soft',
      refund: false,
      reason: ''
    });
    setDeleteOpen(true);
  };

  const handleCreate = async () => {
    if (!createForm.partner_id || !createForm.product_name || !createForm.amount) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/invoices`, {
        ...createForm,
        amount: parseFloat(createForm.amount),
        quantity: parseInt(createForm.quantity)
      });
      toast.success('Invoice created successfully');
      setCreateOpen(false);
      setCreateForm({ partner_id: '', product_name: '', description: '', billing_cycle: 'monthly', quantity: 1, amount: '' });
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/invoices/${selectedInvoice.id}`, {
        ...editForm,
        amount: editForm.amount ? parseFloat(editForm.amount) : undefined,
        quantity: editForm.quantity ? parseInt(editForm.quantity) : undefined
      });
      toast.success('Invoice updated successfully');
      setEditOpen(false);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoid = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/admin/invoices/${selectedInvoice.id}/void?reason=${encodeURIComponent(voidReason)}`);
      toast.success(`Invoice voided. Refund: ₹${response.data.refund_amount.toLocaleString()}`);
      setVoidOpen(false);
      setVoidReason('');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadInvoice = async (invoice) => {
    try {
      const response = await axios.get(`${API}/admin/invoices/${invoice.id}/download`);
      const byteCharacters = atob(response.data.pdf_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice downloaded');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to download invoice';
      toast.error(msg);
    }
  };

  const viewInvoice = async (invoice) => {
    try {
      const response = await axios.get(`${API}/admin/invoices/${invoice.id}`);
      const detailRes = await axios.get(`${API}/invoices/${invoice.id}/view-details`).catch(() => ({ data: {} }));
      setSelectedInvoice({ ...response.data, ...detailRes.data });
      setViewOpen(true);
    } catch (error) {
      toast.error('Failed to fetch invoice details');
    }
  };

  const openEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setEditForm({
      product_name: invoice.product_name,
      description: invoice.description || '',
      billing_cycle: invoice.billing_cycle,
      quantity: invoice.quantity,
      amount: invoice.amount,
      notes: invoice.notes || '',
      status: invoice.status
    });
    setEditOpen(true);
  };

  const openVoid = (invoice) => {
    setSelectedInvoice(invoice);
    setVoidReason('');
    setVoidOpen(true);
  };

  const getStatusBadge = (status) => {
    const statusInfo = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[1];
    const Icon = statusInfo.icon;
    return (
      <Badge className={`${statusInfo.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {statusInfo.label}
      </Badge>
    );
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setPartnerFilter('all');
    setStartDate('');
    setEndDate('');
  };

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap md:flex-nowrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="invoices-title">Invoice Management</h1>
            <p className="text-slate-500 mt-1">View, create, and manage all invoices</p>
          </div>
          <div className="flex gap-2 sm-0 mt-2 md:m-0 sm:ml-3">
            <Button variant="outline" onClick={fetchInvoices} data-testid="refresh-invoices">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {canCreateInvoice && (
              <Button onClick={() => setCreateOpen(true)} data-testid="create-invoice-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 [@media(min-width:1024px)]:grid-cols-4 [@media(min-width:1700px)]:grid-cols-6 gap-4 items-end">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('all'); setShowDeleted(false); }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Invoices</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'paid' && !showDeleted ? 'ring-2 ring-green-500' : ''}`} onClick={() => { setStatusFilter('paid'); setShowDeleted(false); }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Paid</p>
                  <p className="text-2xl font-bold text-green-600">{stats.paid}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'pending' && !showDeleted ? 'ring-2 ring-yellow-500' : ''}`} onClick={() => { setStatusFilter('pending'); setShowDeleted(false); }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'voided' && !showDeleted ? 'ring-2 ring-red-500' : ''}`} onClick={() => { setStatusFilter('voided'); setShowDeleted(false); }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Voided</p>
                  <p className="text-2xl font-bold text-red-600">{stats.voided}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${showDeleted ? 'ring-2 ring-slate-500' : ''}`} onClick={() => { setShowDeleted(true); setStatusFilter('all'); }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Deleted</p>
                  <p className="text-2xl font-bold text-slate-600">{stats.deleted || 0}</p>
                </div>
                <Archive className="h-8 w-8 text-slate-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-blue-600">₹{stats.total_amount?.toLocaleString()}</p>
                </div>
                <DollarSign className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Show Deleted Toggle */}
        {showDeleted && (
          <div className="bg-slate-100 border border-slate-300 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Archive className="h-5 w-5 text-slate-600" />
              <div>
                <p className="font-medium text-slate-700">Viewing Deleted Invoices (Audit Mode)</p>
                <p className="text-sm text-slate-500">Showing soft-deleted invoices for audit purposes</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setShowDeleted(false)}>
              Exit Audit Mode
            </Button>
          </div>
        )}

        {/* ─── Open Invoice Queries ─── */}
        {queries.filter(q => q.status === 'open').length > 0 && (
          <Card className="border-amber-200 bg-amber-50 mt-4">
            <CardContent className="p-4">
              <button
                className="w-full flex items-center justify-between"
                onClick={() => setQueriesOpen(v => !v)}
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-amber-600" />
                  <span className="font-semibold text-amber-800">
                    Open Invoice Queries ({queries.filter(q => q.status === 'open').length})
                  </span>
                </div>
                {queriesOpen ? <ChevronUp className="h-4 w-4 text-amber-600" /> : <ChevronDown className="h-4 w-4 text-amber-600" />}
              </button>
              {queriesOpen && (
                <div className="mt-3 space-y-2">
                  {queries.filter(q => q.status === 'open').map(q => (
                    <div key={q.id} className="bg-white border border-amber-200 rounded-lg p-3 flex items-start justify-between gap-3 mt-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">
                          Invoice #{q.invoice?.invoice_number || q.invoice_id}
                          <span className="ml-2 text-xs text-slate-500">from {q.raised_by?.company_name || q.raised_by?.email || 'Unknown'}</span>
                        </p>
                        <p className="text-sm text-slate-700 mt-0.5 line-clamp-2">{q.reason}</p>
                        <p className="text-xs text-slate-400 mt-1">{new Date(q.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      <Button size="sm" variant="outline" className="shrink-0" onClick={() => { setSelectedQuery(q); setResolveNote(''); setQueryViewOpen(true); }}
                        data-testid={`view-query-${q.id}`}>
                        View & Respond
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="!mb-2">
          <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 items-end">
            {/* 1. Search Input: Mobile aur Tablet par Full Width (col-span-2), Desktop par 2 columns (md:col-span-2) */}
            <div className="col-span-2 md:col-span-2 space-y-1.5">
              <label className="text-xs font-medium text-slate-600 block">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search invoice #, order, partner..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* 2. Status Filter: Mobile/Tablet me 1 column (2 me se half), Desktop me 1 column */}
            <div className="col-span-1 md:col-span-1 space-y-1.5">
              <label className="text-xs font-medium text-slate-600 block">
                Status
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 3. Partner Filter */}
            <div className="col-span-1 md:col-span-1 space-y-1.5">
              <label className="text-xs font-medium text-slate-600 block">
                Partner
              </label>
              <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                <SelectTrigger data-testid="partner-filter">
                  <SelectValue placeholder="Partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Partners</SelectItem>
                  {partners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 4. From Date */}
            <div className="col-span-1 md:col-span-1 space-y-1.5">
              <label className="text-xs font-medium text-slate-600 block">
                From Date
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date"
              />
            </div>

            {/* 5. To Date */}
            <div className="col-span-1 md:col-span-1 space-y-1.5">
              <label className="text-xs font-medium text-slate-600 block">
                To Date
              </label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date"
              />
            </div>
          </div>
            {(search || statusFilter !== 'all' || partnerFilter !== 'all' || startDate || endDate) && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-sm text-slate-500">Filters applied</span>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="bg-blue-100 hover:bg-blue-600 hover:text-white">
                  Clear all
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoices Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : invoices.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No invoices found</h3>
              <p className="text-slate-500 text-sm mt-1">Create your first invoice or adjust filters</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="mt-4">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 text-nowrap">Invoice #</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 text-nowrap">Partner</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 text-nowrap">Product</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 text-nowrap">Order</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 text-nowrap">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 text-nowrap">Amount (+ GST)</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 text-nowrap">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 text-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className={`border-b hover:bg-slate-50 ${invoice.is_deleted ? 'bg-slate-100 opacity-75' : ''}`} data-testid={`invoice-row-${invoice.id}`}>
                        <td className="py-3 px-4 text-nowrap">
                          <span className={`font-medium ${invoice.is_deleted ? 'text-slate-500 line-through' : 'text-blue-600'}`}>{invoice.invoice_number}</span>
                          {invoice.is_custom && (
                            <Badge variant="outline" className="ml-2 text-xs">Custom</Badge>
                          )}
                          {invoice.is_deleted && (
                            <Badge variant="outline" className="ml-2 text-xs bg-slate-200 text-slate-600">Deleted</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-sm">{invoice.partner?.company_name || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{invoice.partner?.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">{invoice.product_name}</td>
                        <td className="py-3 px-4 text-sm">
                          {invoice.order_id ? (
                            <span className="font-mono text-xs">{invoice.order_id.slice(0, 8)}...</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {invoice.is_deleted ? (
                            <div>
                              <p className="text-xs text-slate-500">Deleted:</p>
                              <p>{invoice.deleted_at ? new Date(invoice.deleted_at).toLocaleDateString() : '-'}</p>
                            </div>
                          ) : (
                            new Date(invoice.created_at).toLocaleDateString()
                          )}
                        </td>
                        <td className="py-3 px-4 font-semibold text-blue-600">
                          ₹{Number(invoice.amount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}
                          {(invoice.subtotal != null && invoice.gst_amount != null) && (
                            <p className="text-xs text-slate-400 font-normal mt-0.5">
                              ₹{Number(invoice.subtotal).toLocaleString('en-IN', {minimumFractionDigits:2})} + ₹{Number(invoice.gst_amount).toLocaleString('en-IN', {minimumFractionDigits:2})} GST
                            </p>
                          )}
                          {invoice.is_deleted && invoice.refund_applied && (
                            <p className="text-xs text-green-600 font-normal">Refunded</p>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => viewInvoice(invoice)} title="View" data-testid={`view-${invoice.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {canViewInvoice && invoice.status === 'paid' && (
                              <Button size="sm" variant="ghost" onClick={() => downloadInvoice(invoice)} title="Download PDF"
                                className="text-green-600 hover:text-green-700" data-testid={`download-${invoice.id}`}>
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                            {invoice.is_deleted ? (
                              canDeleteInvoice && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleRestore(invoice)} 
                                  title="Restore" 
                                  className="text-green-600 hover:text-green-700"
                                  data-testid={`restore-${invoice.id}`}
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </Button>
                              )
                            ) : (
                              <>
                                {invoice.status !== 'voided' && (
                                  <>
                                    {canEditInvoice && (
                                      <Button size="sm" variant="ghost" onClick={() => openEdit(invoice)} title="Edit" data-testid={`edit-${invoice.id}`}>
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {canVoidInvoice && (
                                      <Button size="sm" variant="ghost" onClick={() => openVoid(invoice)} title="Void" className="text-amber-600 hover:text-amber-700" data-testid={`void-${invoice.id}`}>
                                        <Ban className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </>
                                )}
                                {canDeleteInvoice && (
                                  <Button size="sm" variant="ghost" onClick={() => openDelete(invoice)} title="Delete" className="text-red-600 hover:text-red-700" data-testid={`delete-${invoice.id}`}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <span className="text-sm text-slate-500">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === pagination.total_pages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create Invoice Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-blue-600" />
                Create Custom Invoice
              </DialogTitle>
              <DialogDescription>
                Create an invoice for custom services or credits
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Partner <span className="text-red-500">*</span></Label>
                <Select value={createForm.partner_id} onValueChange={(v) => setCreateForm(prev => ({ ...prev, partner_id: v }))}>
                  <SelectTrigger data-testid="create-partner-select">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.filter(p => p.role === 'reseller').map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.company_name} ({p.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Product/Service Name <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="e.g., Custom Service, Credit Adjustment"
                  value={createForm.product_name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, product_name: e.target.value }))}
                  data-testid="create-product-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Additional details..."
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Select value={createForm.billing_cycle} onValueChange={(v) => setCreateForm(prev => ({ ...prev, billing_cycle: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_CYCLES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={createForm.quantity}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Amount (₹) <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={createForm.amount}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, amount: e.target.value }))}
                  data-testid="create-amount"
                />
              </div>
              {/* GST type preview based on selected partner */}
              {createForm.partner_id && (() => {
                const selPartner = partners.find(p => p.id === createForm.partner_id);
                const gstType = determineGstType(getEntityGstin(selPartner), getEntityState(selPartner));
                const entityState = getEntityState(selPartner);
                const entityGstin = getEntityGstin(selPartner);
                const baseAmt = parseFloat(createForm.amount) || 0;
                const gstAmt = Math.round(baseAmt * 0.18 * 100) / 100;
                const half = Math.round(gstAmt / 2 * 100) / 100;
                return (
                  <div className="bg-slate-50 border rounded-lg p-3 text-sm space-y-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${gstType === 'CGST+SGST' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {gstType} will be applied
                      </span>
                      {entityState && <span className="text-xs text-slate-400">({entityState})</span>}
                      {!entityState && !entityGstin && (
                        <span className="text-xs text-amber-600">⚠ State not set for this partner — IGST applied by default. Ask them to update their profile.</span>
                      )}
                    </div>
                    <div className="flex justify-between text-slate-500"><span>Sub Total</span><span>₹{baseAmt.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                    {gstType === 'CGST+SGST' ? (
                      <>
                        <div className="flex justify-between text-slate-500"><span>CGST (9%)</span><span>₹{half.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                        <div className="flex justify-between text-slate-500"><span>SGST (9%)</span><span>₹{half.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                      </>
                    ) : (
                      <div className="flex justify-between text-slate-500"><span>IGST (18%)</span><span>₹{gstAmt.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                    )}
                    <div className="flex justify-between font-semibold border-t pt-1"><span>Total</span><span className="text-blue-700">₹{(baseAmt + gstAmt).toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                  </div>
                );
              })()}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <p className="font-medium flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" /> Note:
                </p>
                <p className="mt-1">Custom invoices are created with <b>Pending</b> status. Mark as Paid after payment is received.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={submitting} data-testid="submit-create">
                {submitting ? 'Creating...' : 'Create Invoice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Invoice Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-600" />
                Edit Invoice
              </DialogTitle>
              <DialogDescription>
                {selectedInvoice?.invoice_number}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Product/Service Name</Label>
                <Input
                  value={editForm.product_name || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, product_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Select value={editForm.billing_cycle} onValueChange={(v) => setEditForm(prev => ({ ...prev, billing_cycle: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_CYCLES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editForm.quantity || 1}
                    onChange={(e) => setEditForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.amount || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, amount: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  placeholder="Internal notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={handleEdit} disabled={submitting} data-testid="submit-edit">
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Invoice Dialog */}
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Invoice Details
              </DialogTitle>
              <DialogDescription className="sr-only">
                View complete invoice details
              </DialogDescription>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-4 py-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-slate-500">Invoice Number</p>
                    <p className="font-semibold text-lg">{selectedInvoice.invoice_number}</p>
                  </div>
                  {getStatusBadge(selectedInvoice.status)}
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2 flex items-center gap-1">
                    <Building2 className="h-4 w-4" /> Partner
                  </p>
                  <div className="bg-[#2563eb]/10 p-3 rounded-lg text-sm">
                    <p><span className="text-slate-500">Company:</span> {selectedInvoice.partner?.company_name}</p>
                    <p><span className="text-slate-500">Email:</span> {selectedInvoice.partner?.email}</p>
                    {selectedInvoice.partner?.gst_number && (
                      <p><span className="text-slate-500">GST:</span> {selectedInvoice.partner?.gst_number}</p>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Product</p>
                    <p className="font-medium">{selectedInvoice.product_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Billing Cycle</p>
                    <p className="font-medium capitalize">{selectedInvoice.billing_cycle?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Quantity</p>
                    <p className="font-medium">{selectedInvoice.quantity}</p>
                  </div>
                  <div className="col-span-2 mt-1 border rounded-lg overflow-hidden text-sm">
                    {(() => {
                      const sub = selectedInvoice.subtotal || (selectedInvoice.amount / 1.18);
                      const gstAmt = selectedInvoice.gst_amount || (selectedInvoice.amount * 0.18 / 1.18);
                      const total = selectedInvoice.total || selectedInvoice.amount;
                      const gstType = selectedInvoice.gst_type || 'IGST';
                      const half = Math.round(gstAmt / 2 * 100) / 100;
                      const entityState = selectedInvoice.entity_state_at_time_of_creation || '';
                      return (
                        <>
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${gstType === 'CGST+SGST' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                              {gstType} Applied
                            </span>
                            {entityState && <span className="text-xs text-slate-400">({entityState})</span>}
                            {!entityState && <span className="text-xs text-amber-600">⚠ State unknown — IGST applied</span>}
                          </div>
                          <div className="flex justify-between px-3 py-1.5 bg-slate-50"><span className="text-slate-500">Sub Total</span><span>₹{sub?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                          {gstType === 'CGST+SGST' ? (
                            <>
                              <div className="flex justify-between px-3 py-1.5"><span className="text-slate-500">CGST (9%)</span><span>₹{half?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                              <div className="flex justify-between px-3 py-1.5"><span className="text-slate-500">SGST (9%)</span><span>₹{half?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                            </>
                          ) : (
                            <div className="flex justify-between px-3 py-1.5"><span className="text-slate-500">IGST (18%)</span><span>₹{gstAmt?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                          )}
                          {selectedInvoice.credit > 0 && <div className="flex justify-between px-3 py-1.5 text-green-600"><span>Credit</span><span>-₹{selectedInvoice.credit?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>}
                          <div className="flex justify-between px-3 py-2 bg-blue-50 font-bold"><span>Total</span><span className="text-blue-700">₹{total?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span></div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {selectedInvoice.order_id && (
                  <div className="border-t pt-4">
                    <p className="text-sm text-slate-500">Linked Order</p>
                    <p className="font-mono text-sm">{selectedInvoice.order_id}</p>
                  </div>
                )}

                {selectedInvoice.description && (
                  <div className="border-t pt-4">
                    <p className="text-sm text-slate-500">Description</p>
                    <p className="text-sm">{selectedInvoice.description}</p>
                  </div>
                )}

                {selectedInvoice.notes && (
                  <div className="border-t pt-4">
                    <p className="text-sm text-slate-500">Notes</p>
                    <p className="text-sm">{selectedInvoice.notes}</p>
                  </div>
                )}

                {selectedInvoice.status === 'voided' && (
                  <div className="border-t pt-4 bg-red-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-red-700">Voided</p>
                    <p className="text-sm text-red-600">
                      {selectedInvoice.void_reason || 'No reason provided'}
                    </p>
                    <p className="text-xs text-red-500 mt-1">
                      {selectedInvoice.voided_at && new Date(selectedInvoice.voided_at).toLocaleString()}
                    </p>
                  </div>
                )}

                <div className="text-xs text-slate-500 border-t pt-4">
                  <p>Created: {new Date(selectedInvoice.created_at).toLocaleString()}</p>
                  {selectedInvoice.updated_at && (
                    <p>Updated: {new Date(selectedInvoice.updated_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setViewOpen(false)}>Close</Button>
              {selectedInvoice?.status !== 'paid' && (
                <span className="text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200 self-center">
                  PDF available once payment is confirmed
                </span>
              )}
              {selectedInvoice?.status === 'paid' && (
                <Button onClick={() => downloadInvoice(selectedInvoice)}
                  className="bg-green-600 hover:bg-green-700 text-white" data-testid="admin-dialog-download-pdf">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Void Invoice Confirmation Dialog */}
        <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Void Invoice
              </DialogTitle>
              <DialogDescription>
                This action will void {selectedInvoice?.invoice_number}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
                <p className="font-medium text-red-800">Warning:</p>
                <ul className="list-disc list-inside mt-2 text-red-700 space-y-1">
                  <li>The invoice will be marked as voided</li>
                  <li>A refund of <b>₹{selectedInvoice?.amount?.toLocaleString()}</b> will be credited to the partner's wallet</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>
              <div className="space-y-2">
                <Label>Reason for voiding</Label>
                <Textarea
                  placeholder="Enter reason for voiding this invoice..."
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  rows={3}
                  data-testid="void-reason"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setVoidOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleVoid} disabled={submitting} data-testid="confirm-void">
                {submitting ? 'Voiding...' : 'Void Invoice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Invoice Dialog */}
        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertOctagon className="h-5 w-5" />
                Delete Invoice
              </DialogTitle>
              <DialogDescription>
                Choose how to delete {selectedInvoice?.invoice_number}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Delete Type Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Delete Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      deleteForm.delete_type === 'soft'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setDeleteForm(prev => ({ ...prev, delete_type: 'soft' }))}
                    data-testid="delete-type-soft"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Archive className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">Soft Delete</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Hides from UI but keeps in database for audits. Can be restored.
                    </p>
                  </div>
                  <div
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      deleteForm.delete_type === 'hard'
                        ? 'border-red-500 bg-red-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setDeleteForm(prev => ({ ...prev, delete_type: 'hard' }))}
                    data-testid="delete-type-hard"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Trash2 className="h-5 w-5 text-red-600" />
                      <span className="font-medium">Hard Delete</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Permanently removes from database. Cannot be restored.
                    </p>
                  </div>
                </div>
              </div>

              {/* Refund Option for Paid Invoices */}
              {selectedInvoice?.status === 'paid' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-amber-800 flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Refund Partner Wallet
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        This is a paid invoice (₹{selectedInvoice.amount?.toLocaleString()}). 
                        Do you want to refund the amount to the partner's wallet?
                      </p>
                    </div>
                    <Switch
                      checked={deleteForm.refund}
                      onCheckedChange={(checked) => setDeleteForm(prev => ({ ...prev, refund: checked }))}
                      data-testid="refund-toggle"
                    />
                  </div>
                  {deleteForm.refund && (
                    <div className="mt-3 p-2 bg-green-100 rounded text-sm text-green-800">
                      ₹{selectedInvoice.amount?.toLocaleString()} will be credited to partner's wallet
                    </div>
                  )}
                </div>
              )}

              {/* Warning based on delete type */}
              <div className={`border rounded-lg p-4 text-sm ${
                deleteForm.delete_type === 'hard' 
                  ? 'bg-red-50 border-red-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <p className={`font-medium ${deleteForm.delete_type === 'hard' ? 'text-red-800' : 'text-blue-800'}`}>
                  {deleteForm.delete_type === 'hard' ? '⚠️ Warning:' : 'ℹ️ Note:'}
                </p>
                <ul className={`list-disc list-inside mt-2 space-y-1 ${
                  deleteForm.delete_type === 'hard' ? 'text-red-700' : 'text-blue-700'
                }`}>
                  {deleteForm.delete_type === 'soft' ? (
                    <>
                      <li>Invoice will be hidden from normal views</li>
                      <li>Invoice remains in database for audit purposes</li>
                      <li>Can be viewed in "Deleted" tab and restored anytime</li>
                      {deleteForm.refund && <li>Wallet will be refunded ₹{selectedInvoice?.amount?.toLocaleString()}</li>}
                    </>
                  ) : (
                    <>
                      <li>Invoice will be <b>permanently deleted</b></li>
                      <li>This action <b>cannot be undone</b></li>
                      <li>Invoice data will be logged in Activity Logs for reference</li>
                      {deleteForm.refund && <li>Wallet will be refunded ₹{selectedInvoice?.amount?.toLocaleString()}</li>}
                    </>
                  )}
                </ul>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label>Reason for deletion <span className="text-red-500">*</span></Label>
                <Textarea
                  placeholder="Enter reason for deleting this invoice..."
                  value={deleteForm.reason}
                  onChange={(e) => setDeleteForm(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  data-testid="delete-reason"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
              <Button 
                variant={deleteForm.delete_type === 'hard' ? 'destructive' : 'default'}
                onClick={handleDelete} 
                disabled={submitting || !deleteForm.reason}
                data-testid="confirm-delete"
              >
                {submitting ? 'Deleting...' : (deleteForm.delete_type === 'hard' ? 'Permanently Delete' : 'Soft Delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* ─── Invoice Query View Dialog ─── */}
      <Dialog open={queryViewOpen} onOpenChange={setQueryViewOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice Query</DialogTitle>
            <DialogDescription>
              Review and respond to the query raised on invoice #{selectedQuery?.invoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          {selectedQuery && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg p-3 space-y-1 text-sm bg-[#115ab4]/10">
                <p><span className="text-slate-500">Invoice:</span> <strong>{selectedQuery.invoice?.invoice_number}</strong> — ₹{selectedQuery.invoice?.amount?.toLocaleString()}</p>
                <p><span className="text-slate-500">Raised by:</span> {selectedQuery.raised_by?.company_name || selectedQuery.raised_by?.email}</p>
                <p><span className="text-slate-500">Role:</span> {selectedQuery.raised_by_role}</p>
                <p><span className="text-slate-500">Date:</span> {new Date(selectedQuery.created_at).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 mb-1">Query Reason:</p>
                <p className="text-sm bg-amber-50 border border-amber-200 rounded p-3">{selectedQuery.reason}</p>
              </div>
              <div className="space-y-1">
                <Label>Your Response (optional)</Label>
                <Textarea
                  placeholder="Explain your resolution or rejection reason..."
                  value={resolveNote}
                  onChange={e => setResolveNote(e.target.value)}
                  rows={3}
                  data-testid="query-response-note"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setQueryViewOpen(false)}>Cancel</Button>
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-600 hover:border-red-600"
              disabled={resolveSubmitting} onClick={() => handleResolveQuery('reject')} data-testid="reject-query-btn">
              {resolveSubmitting ? 'Processing...' : 'Reject Query'}
            </Button>
            <Button disabled={resolveSubmitting} onClick={() => handleResolveQuery('resolve')} data-testid="resolve-query-btn">
              {resolveSubmitting ? 'Processing...' : 'Mark Resolved'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
